import { Checkbox, TableCell, TableRow, Tooltip } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { InstrumentIcon } from '../common/InstrumentIcon'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import {
  TRADER_DIARY_CANCELED,
  TRADER_DIARY_PARTIAL_SETTLED,
  TRADER_DIARY_RECEIVED,
  TRADER_DIARY_SETTLED,
} from '../../constants/codeLists'

import { newThemeObject } from '../../newTheme'
import { formatNumber } from '../../utils/numbersFormatter'
import { ColoredIconButton } from '../common/ColoredIconButton'

import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import CancelOutlinedIcon from '@mui/icons-material/CancelRounded';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightRounded';
import { MASS_ORDER_INPUT_DIALOG_ID } from '../../constants/dialogConstants'
import { TraderDiary, TraderDiaryTableRow } from '../../redux/reducers/traderDiary'
import { newTraderDiary } from '../../constants/initialStates'
import { tokens } from '../../styles/tokens'

const useStyles = makeStyles(theme => ({
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  rowHover: {
    transition: `background-color ${tokens.transition.fast}`,
    '&:hover': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  rowOrderState: {
    borderRadius: '1rem',
    height: '2rem',
    width: '100px',
    alignContent: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  rowOrderStateText: {
    opacity: '0.5',
    fontWeight: '500',
    fontSize: '0.7rem',
    margin: 0,
  },
  tableContentIcon: {
    padding: '25px 20px',
  },
  sendToFix: {
    justifyContent: 'center',
    align: 'center',
    display: 'flex',
  },
  columnCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    color: tokens.color.neutral[900],
  },
  rowColorSell: {
    backgroundColor: tokens.color.sellRow,
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
  },
  tableCell: {
    paddingLeft: 0,
  },
  editButton: {
    marginLeft: 6,
    backgroundColor: tokens.color.error[500],
  }
}))

interface TraderDiaryTableRowProps {
  row: TraderDiaryTableRow
  isLast: boolean,
  setTraderDiaryCheck: (orderId: number, valueDiary: boolean) => any
  setTraderDiary: (traderDiary: TraderDiary) => any
  setOrderAlertOpen: (orderOpen: boolean) => any
}

const TableRowDiary = ({ row, isLast, setTraderDiaryCheck, setTraderDiary, setOrderAlertOpen}: TraderDiaryTableRowProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const orderDirectionValue = (orderDirection: string) => {
    if (orderDirection === 'OD_BUY') {
      return t('OD_BUY')
    } else {
      return t('OD_SELL')
    }
  }

  const orderValidityType = (validityType: string, validityDate: string) => {
    let result
    if (validityType === 'OV_GTD') {
      result = validityDate ? format(new Date(validityDate), 'dd/MM/yyyy') : ''
    } else {
      result = ''
    }
    return result
  }

  const marketTypeValue = (marketType: string) => {
    if (marketType === 'OMT_LIMIT') {
      return t('OMT_LIMIT')
    } else {
      return t('OMT_MARKET')
    }
  }

  const orderValidityValue = (orderValidity: string | number) => {
    if (orderValidity === 'OV_GTC') {
      return t('OV_GTC')
    } else {
      return t('OV_GTD')
    }
  }

  const getStateColor = (state: string) => {
    if (state === TRADER_DIARY_RECEIVED ) {
      return newThemeObject.palette.state.new
    } else if (state === TRADER_DIARY_PARTIAL_SETTLED ) {
      return newThemeObject.palette.state.partiallyFilled
    } else if ( state === TRADER_DIARY_SETTLED ) {
      return newThemeObject.palette.state.filled
    } else if ( state === TRADER_DIARY_CANCELED ) {
      return newThemeObject.palette.state.canceled
    }
  }

  return (
    <TableRow
      className={classNames([classes.tableContent, classes.rowHover, row.orderDirection === 'OD_SELL' ? classes.rowColorSell : ''])}
      key={row.traderDiaryId}
    >
      <TableCell
        className={classNames([classes.tableContent, classes.tableContentIcon])}
        style={isLast ? { borderBottom: '0' } : {}}
      >
        <InstrumentIcon securityType={row.assetClass} />
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <p className={classes.text}>{format(new Date(row.creationDate), 'dd/MM/yyyy')}</p>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div className={classes.columnCell}>
          {row.companyName && <p className={classes.text}>{row.companyName}</p>}
          {!row.companyName && <>
            <p className={classes.text}>{row.clientSurname}</p>
            <p className={classes.text}>{row.clientFirstname}</p>
          </>}
        </div>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div className={classes.columnCell}>
          <p className={classes.text}>{orderDirectionValue(row.orderDirection)}</p>
          <p className={classes.text}>{marketTypeValue(row.orderMarketType)}</p>
        </div>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div className={classes.columnCell}>
          <p className={classes.text}>{orderValidityValue(row.validityType)}</p>
          <p className={classes.text}>{orderValidityType(row.validityType, row.validity)}</p>
        </div>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div className={classes.columnCell}>
          <p className={classes.text}>{formatNumber(row.price)}</p>
          <p className={classes.text}>
            {formatNumber(row.quantity, {
              decimalPlaces: 0,
              suffix: `${t('UNIT')}`,
            })}
          </p>
        </div>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div className={classes.columnCell}>
          <p className={classes.text}>{formatNumber(row.amount)}</p>
          <p className={classes.text}>{row.currency}</p>
        </div>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div className={classes.columnCell}>
          <p className={classes.text}>{row.securityName}</p>
          <p className={classes.text}>
            {row.isin}
            {row.symbol ? `/${row.symbol}` : ''}
          </p>
        </div>
      </TableCell>
      <TableCell className={classes.tableCell} style={isLast ? { borderBottom: '0' } : {}}>
        <div style={{ backgroundColor: getStateColor(row.state) }} className={classes.rowOrderState}>
          <p className={classes.rowOrderStateText}>{t(row.state)}</p>
        </div>
      </TableCell>
      <TableCell>
        <Checkbox
          disabled={row.diaryExported}
          checked={row.diary}
          onChange={()=>setTraderDiaryCheck(row.traderDiaryId, row.diary)}
          name="checked"
          color="primary"
        />
      </TableCell>

      <TableCell align="right">
        <ColoredIconButton
          size="small"
          onClick={() => {
              setTraderDiary(getTraderDiariesFromRow(row));
              dispatch(
                openDialog(MASS_ORDER_INPUT_DIALOG_ID, {
                  clientId: row.clientId,
                  securityId: row.securityId,
                  orderId: row.traderDiaryId,
                }))
            }
          }
          icon={<ArrowCircleRightOutlinedIcon fontSize="small" />}
          title={t('REALIZE_ORDER')}
          disabled={row.state!==TRADER_DIARY_RECEIVED}
        />
        <ColoredIconButton
          className={classes.editButton}
          size="small"
          onClick={() => {
            setTraderDiary(getTraderDiariesFromRow(row));
            setOrderAlertOpen(true)
          }}
          icon={<CancelOutlinedIcon fontSize="small" />}
          title={t('TRADER_CANCEL')}
          disabled={row.state!==TRADER_DIARY_RECEIVED}
        />
      </TableCell>
    </TableRow>
  )
}

const getTraderDiariesFromRow = (row: TraderDiaryTableRow) => {
  // kopie, ne reference - jinak se prepisuje sdilena konstanta newTraderDiary
  // a novy zapis do deniku se predvyplni cenou posledniho realizovaneho radku
  let tmp : TraderDiary = { ...newTraderDiary };

  tmp.traderDiaryId = row.traderDiaryId;
  tmp.businessTerritoryId = row.businessTerritoryId;
  tmp.creationDate = row.creationDate
  tmp.amount = row.amount;
  tmp.quantity = row.quantity;
  tmp.price = row.price;
  tmp.state = row.state;
  
  tmp.orderDirection = row.orderDirection;
  tmp.validityType = row.validityType;
  tmp.validity = new Date(row.validity);
  tmp.orderType = row.orderType;
  tmp.orderMarketType = row.orderMarketType;

  tmp.currency = row.currency;
  tmp.securityId = row.securityId;

  tmp.portfolio = row.portfolio
  tmp.clientId = row.clientId
  tmp.methodReceiving = row.methodReceiving

  return tmp;
}

export default TableRowDiary
